import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { ListItem } from 'src/components/ListItem';
import { Typography } from 'src/components/Typography';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';

import type { Appointment } from 'src/models/appointment/_types';

interface BulkActionsProps {
  appointment: Appointment;
  isLoading?: boolean;
  // Bulk action handlers
  onBulkConfirmGoing?: () => void;
  onBulkComplete?: () => void;
  onBulkCancel?: () => void;
  onBulkLateCancelNoFee?: () => void;
  onBulkNoShowNoFee?: () => void;
  onBulkResetStatus?: () => void;
  onBulkChargeDefaultPackage?: () => void;
  // TODO: These need separate dialogs/flows
  onLogWorkouts?: () => void;
  onMessageAll?: () => void;
}

/**
 * BulkActions - Dropdown menu for bulk visit actions
 *
 * Matches v2 modal-new-appointment.hbs lines 616-656 (FoundationDrop "Bulk Manage All")
 *
 * Actions:
 * 1. Log Workout(s) for Everyone - if planWorkoutIds and visits without workoutId
 * 2. Message All - opens message dialog
 * 3. Confirm (Going) Everyone - status: registered
 * 4. Complete (Check-In) Everyone - status: completed
 * 5. Cancel Everyone - status: cancelled
 * 6. Late Cancel (No Fee) Everyone - status: late_cancelled
 * 7. No Show (No Fee) Everyone - status: noshowed
 * 8. Reset Everyone's Status - status: ""
 * 9. Bulk Charge Default Package - if service.defaultPackageId
 */
export const BulkActions: FC<BulkActionsProps> = ({
  appointment,
  isLoading = false,
  onBulkConfirmGoing,
  onBulkComplete,
  onBulkCancel,
  onBulkLateCancelNoFee,
  onBulkNoShowNoFee,
  onBulkResetStatus,
  onBulkChargeDefaultPackage,
  onLogWorkouts,
  onMessageAll,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.viewEventDialog.bulkActions',
  });
  const { canCancelVisit } = usePermissionCheck();
  const visits = appointment.visits || [];
  const activeVisits = visits.filter(
    (v) => v.status !== 'cancelled' && v.status !== 'late_cancelled'
  );
  const hasActiveVisits = activeVisits.length > 0;
  const hasVisits = visits.length > 0;
  const service = appointment.service;

  // Show "Log Workouts" if appointment has planWorkoutIds and any visits don't have a workoutId yet
  // v2 computed: showLogWorkouts (lines 86-95)
  const hasPlanWorkouts =
    appointment.planWorkoutIds && appointment.planWorkoutIds.length > 0;
  const hasVisitsWithoutWorkout = visits.some((v) => !v.workoutId);
  const showLogWorkouts =
    hasPlanWorkouts && visits.length > 0 && hasVisitsWithoutWorkout;

  // Service fee info for labels
  const hasNoshowFee = service?.noshowFee;
  const hasCancellationFee = service?.cancellationFee;
  const hasDefaultPackage = service?.defaultPackageId;

  if (!hasVisits) {
    return null;
  }

  return (
    <DropdownMenu
      aboveDialog
      trigger={
        <Button
          variant="bordered"
          size="small"
          disabled={isLoading}
          endIcon={<Icon icon={faChevronDown} />}
        >
          {t('title')}
        </Button>
      }
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {hasActiveVisits && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('description')}
          </Typography>
        </Box>
      )}
      {/* Log Workout(s) for Everyone - v2 lines 624-630 */}
      {showLogWorkouts && hasActiveVisits && (
        <ListItem
          primary={t('logWorkoutsForEveryone')}
          onClick={onLogWorkouts}
          disabled={!onLogWorkouts}
        />
      )}
      {/* Message All - v2 line 632 */}
      {hasActiveVisits && (
        <ListItem
          primary={t('messageAll')}
          onClick={onMessageAll}
          disabled={!onMessageAll}
        />
      )}
      {/* Confirm (Going) Everyone - v2 lines 633-634 */}
      {hasActiveVisits && (
        <ListItem
          primary={t('confirmEveryone')}
          onClick={onBulkConfirmGoing}
          disabled={!onBulkConfirmGoing || isLoading}
        />
      )}
      {/* Complete (Check-In) Everyone - v2 lines 635-636 */}
      {hasActiveVisits && (
        <ListItem
          primary={t('completeEveryone')}
          onClick={onBulkComplete}
          disabled={!onBulkComplete || isLoading}
        />
      )}
      {/* Cancel Everyone - v2 lines 637-638 */}
      {canCancelVisit && hasActiveVisits && (
        <ListItem
          primary={t('cancelEveryone')}
          onClick={onBulkCancel}
          disabled={!onBulkCancel || isLoading}
        />
      )}
      {/* Late Cancel Everyone - v2 lines 639-643 */}
      {canCancelVisit && hasActiveVisits && (
        <ListItem
          primary={hasCancellationFee ? t('lateCancelNoFee') : t('lateCancel')}
          onClick={onBulkLateCancelNoFee}
          disabled={!onBulkLateCancelNoFee || isLoading}
        />
      )}
      {/* No Show Everyone - v2 lines 644-647 */}
      {hasActiveVisits && (
        <ListItem
          primary={hasNoshowFee ? t('noShowNoFee') : t('noShow')}
          onClick={onBulkNoShowNoFee}
          disabled={!onBulkNoShowNoFee || isLoading}
        />
      )}
      {/* Reset Everyone's Status - v2 line 648 */}
      <ListItem
        primary={t('resetEveryoneStatus')}
        onClick={onBulkResetStatus}
        disabled={!onBulkResetStatus || isLoading}
      />
      {/* Bulk Charge Default Package - v2 lines 649-654 */}
      {hasDefaultPackage && hasActiveVisits && (
        <ListItem
          primary={t('bulkChargeDefaultPackage')}
          onClick={onBulkChargeDefaultPackage}
          disabled={!onBulkChargeDefaultPackage || isLoading}
        />
      )}
    </DropdownMenu>
  );
};
